import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { PaginationFooter } from '@/components/shared/PaginationFooter';
import { Search, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export default function ComplianceDashboardPage() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      const params = new URLSearchParams({ page, pageSize: 20 });
      if (statusFilter !== 'all') params.set('overallStatus', statusFilter);
      const res = await api.get(`/compliance/staff?${params}`);
      setData(res.data.items || res.data.data || []);
      setTotal(res.data.total || 0);
    } catch (e) { toast.error('Failed to load compliance data'); }
  };
  useEffect(() => { fetchData(); }, [page, statusFilter]); // eslint-disable-line

  const filtered = data.filter(d => !search || d.staff_name?.toLowerCase().includes(search.toLowerCase()));

  const exportCSV = async () => {
    try {
      const res = await api.get('/compliance/export-csv', { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data])); const a = document.createElement('a');
      a.href = url; a.download = 'compliance.csv'; a.click(); toast.success('Exported');
    } catch (e) { toast.error('Export failed'); }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold tracking-tight">Staff Compliance</h1><p className="text-muted-foreground mt-1">Compliance pack status overview</p></div>
        <Button variant="outline" onClick={exportCSV}><Download className="h-4 w-4 mr-2" /> Export CSV</Button>
      </div>
      <div className="flex items-center gap-3">
        <div className="relative flex-1 md:w-[280px] md:flex-none"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" /><Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" /></div>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-[180px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Statuses</SelectItem><SelectItem value="COMPLIANT">Compliant</SelectItem><SelectItem value="EXPIRING_SOON">Expiring Soon</SelectItem><SelectItem value="NON_COMPLIANT">Non-Compliant</SelectItem></SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground">{total} record(s)</span>
      </div>
      <Card><CardContent className="p-0">
        <Table>
          <TableHeader><TableRow><TableHead>Staff</TableHead><TableHead>Email</TableHead><TableHead>Status</TableHead><TableHead>Missing</TableHead><TableHead>Expiring</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No records</TableCell></TableRow>
            : filtered.map(d => {
              const comp = d.computed || {};
              return (
                <TableRow key={d.id} className="hover:bg-muted/60 cursor-pointer" onClick={() => navigate(`/staff/${d.staff_id}`)}>
                  <TableCell className="font-medium">{d.staff_name || 'N/A'}</TableCell>
                  <TableCell className="text-muted-foreground text-sm">{d.email || '-'}</TableCell>
                  <TableCell><StatusBadge status={comp.overall_status || 'UNKNOWN'} /></TableCell>
                  <TableCell className="text-xs">{(comp.missing_mandatory_keys || []).join(', ') || '-'}</TableCell>
                  <TableCell className="text-xs">{(comp.expiring_soon_keys || []).join(', ') || '-'}</TableCell>
                  <TableCell><Button variant="ghost" size="sm">View</Button></TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <PaginationFooter page={page} pageSize={20} total={total} onPageChange={setPage} />
      </CardContent></Card>
    </div>
  );
}
